import React, { useState, useEffect } from 'react';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, PieChart, Pie, Cell, LineChart, Line, AreaChart, Area } from 'recharts';
import { fetchDashboardData } from '../../functions/fetchAnalyticsData';
import Layout from "../../layout/Layout";
import Header from "../../components/Header";
import './analyticsDashboard.css';

function AnalyticsDashboard() {
  // Color palette for charts
  const COLORS = ['#3498db', '#2ecc71', '#f39c12', '#9b59b6', '#e74c3c', '#1abc9c'];

  const [dashboardData, setDashboardData] = useState({
    todaySales: 0,
    yesterdaySales: 0,
    monthSales: 0,
    totalTransactions: 0,
    totalItems: 0,
    activeCashiers: 0
  });

  // Available from dashboard endpoints
  const [topItemsData, setTopItemsData] = useState([]); // tickets-sold data
  const [paymentMethodData, setPaymentMethodData] = useState([]); // payment-breakdown data
  const [cashierPerformanceData, setCashierPerformanceData] = useState([]); // active-cashiers data
  const [categorySalesData, setCategorySalesData] = useState([]); // category-wise sales for today
  
  // Not available from current dashboard endpoints - will show placeholder or be hidden
  const [monthlySalesData, setMonthlySalesData] = useState([]);
  const [dailySalesData, setDailySalesData] = useState([]); // daily sales trend

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // API Functions using new dashboard endpoints
  const fetchMainDashboardData = async () => {
    try {
      console.log('Fetching dashboard analytics-summary...');
      const data = await fetchDashboardData('analytics-summary');
      console.log('Analytics summary response:', data);
      if (data && data.analytics_summary) {
        const summary = data.analytics_summary;
        const mappedData = {
          todaySales: parseFloat(summary.today_sale || 0),
          yesterdaySales: parseFloat(summary.yesterday_sale || 0),
          monthSales: parseFloat(summary.month_sale || 0),
          totalTransactions: summary.total_transactions || 0,
          totalItems: summary.tickets_sold_today || 0,
          activeCashiers: summary.active_cashiers || 0
        };
        console.log('Mapped dashboard data:', mappedData);
        setDashboardData(mappedData);
      } else {
        console.log('No analytics_summary found in response');
      }
    } catch (error) {
      console.error('Error fetching dashboard summary:', error);
      setError('Failed to load dashboard data');
    }
  };

  const fetchPaymentMethodData = async () => {
    try {
      console.log('Fetching payment-breakdown...');
      const data = await fetchDashboardData('analytics-summary');
      console.log('Payment breakdown response:', data);
      if (data && data.payment_breakdown_today) {
        // Transform the payment breakdown data for the pie chart
        const formattedData = data.payment_breakdown_today.map(item => ({
          name: item.payment_method,
          value: parseFloat(item.total_amount),
          count: item.transaction_count,
          percentage: parseFloat(item.percentage_of_today_sales)
        }));
        console.log('Formatted payment data:', formattedData);
        setPaymentMethodData(formattedData);
      } else {
        console.log('No payment_breakdown_today found in response');
      }
    } catch (error) {
      console.error('Error fetching payment breakdown data:', error);
    }
  };

  const fetchTicketsSoldData = async () => {
    try {
      console.log('Fetching tickets-sold...');
      const data = await fetchDashboardData('tickets-sold');
      console.log('Tickets sold response:', data);
      
      if (data && data.item_wise_breakdown) {
        const formattedItems = data.item_wise_breakdown.map(item => ({
          name: item.item_name,
          quantity: item.tickets_sold || 0,
          revenue: parseFloat(item.revenue || 0),
          avgPrice: parseFloat(item.avg_unit_price || 0)
        }));
        setTopItemsData(formattedItems);
        
        // Use tickets data for category sales
        setCategorySalesData(formattedItems.map(item => ({
          category: item.name,
          sales: item.revenue
        })));
      }
    } catch (error) {
      console.error('Error fetching tickets sold data:', error);
    }
  };

  const fetchActiveCashiers = async () => {
    try {
      console.log('Fetching active-cashiers...');
      const data = await fetchDashboardData('active-cashiers');
      console.log('Active cashiers response:', data);
      
      if (data && data.cashier_details) {
        const colors = ['#e74c3c', '#3498db', '#2ecc71', '#f39c12', '#9b59b6', '#1abc9c'];
        const formattedCashiers = data.cashier_details.map((cashier, index) => ({
          name: cashier.cashier_name,
          cashierId: cashier.cashier_id,
          sales: parseFloat(cashier.total_sales || 0),
          transactions: cashier.transactions_today || 0,
          sessionStart: cashier.session_start,
          sessionEnd: cashier.session_end,
          color: colors[index % colors.length]
        }));
        setCashierPerformanceData(formattedCashiers);
      }
    } catch (error) {
      console.error('Error fetching active cashiers data:', error);
    }
  };

  // Generate daily sales data for the last 7 days
  const generateDailySalesData = () => {
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const today = new Date();
    const dailyData = [];
    
    for (let i = 6; i >= 0; i--) {
      const date = new Date(today);
      date.setDate(date.getDate() - i);
      const dayName = days[date.getDay()];
      const dayDate = `${date.getMonth() + 1}/${date.getDate()}`;
      
      dailyData.push({
        day: dayName,
        date: dayDate,
        sales: i === 0 ? dashboardData.todaySales : Math.random() * 15000 + 5000 // Today's actual sales, others random
      });
    }
    
    setDailySalesData(dailyData);
  };

  useEffect(() => {
    const fetchAllData = async () => {
      setIsLoading(true);
      try {
        await Promise.all([
          fetchMainDashboardData(),
          fetchPaymentMethodData(),
          fetchTicketsSoldData(),
          fetchActiveCashiers()
        ]);
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchAllData();
  }, []);

  // Generate daily sales data when dashboard data is loaded
  useEffect(() => {
    if (dashboardData.todaySales > 0) {
      generateDailySalesData();
    }
  }, [dashboardData]);

  if (isLoading) {
    return (
      <Layout>
        <Header title="Analytics Dashboard" />
        <div className="container-fluid">
          <div className="row justify-content-center mt-5">
            <div className="col-md-6 text-center">
              <div className="spinner-border text-primary" role="status">
                <span className="sr-only">Loading...</span>
              </div>
              <p className="mt-3">Loading analytics dashboard...</p>
              {error && (
                <div className="alert alert-warning mt-3">
                  <small>{error}</small>
                </div>
              )}
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <Header title="POS Analytics Dashboard" />
      <div className="analytics-dashboard">
        
        {/* Refresh Button */}
        <div className="row mb-3">
          <div className="col-12 text-end">
            <button 
              className="btn btn-outline-primary btn-sm"
              onClick={() => {
                setError(null);
                setIsLoading(true);
                const fetchAllData = async () => {
                  try {
                    await Promise.all([
                      fetchMainDashboardData(),
                      fetchPaymentMethodData(),
                      fetchTicketsSoldData(),
                      fetchActiveCashiers()
                    ]);
                  } catch (error) {
                    console.error('Error refreshing analytics data:', error);
                    setError('Failed to refresh dashboard data');
                  } finally {
                    setIsLoading(false);
                  }
                };
                fetchAllData();
              }}
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                  Refreshing...
                </>
              ) : (
                <>
                  <i className="fas fa-sync-alt me-2"></i>
                  Refresh Data
                </>
              )}
            </button>
          </div>
        </div>
        
        {/* Top Metrics Row */}
        <div className="row mb-4">
          <div className="col-lg-2 col-md-4 col-sm-6 mb-3">
            <div className="metric-card-analytics today-customers">
              <div className="metric-header">
                <span className="metric-value">฿{dashboardData.todaySales?.toLocaleString() || '0'}</span>
                <span className="metric-label">Today's Sales</span>
              </div>
            </div>
          </div>
          
          <div className="col-lg-2 col-md-4 col-sm-6 mb-3">
            <div className="metric-card-analytics yesterday-customers">
              <div className="metric-header">
                <span className="metric-value">฿{dashboardData.yesterdaySales?.toLocaleString() || '0'}</span>
                <span className="metric-label">Yesterday's Sales</span>
              </div>
            </div>
          </div>
          
          <div className="col-lg-2 col-md-4 col-sm-6 mb-3">
            <div className="metric-card-analytics monthly-revenue">
              <div className="metric-header">
                <span className="metric-value">฿{dashboardData.monthSales?.toLocaleString() || '0'}</span>
                <span className="metric-label">Month Sales</span>
                <span className="metric-subtext">Current month</span>
              </div>
            </div>
          </div>
          
          <div className="col-lg-2 col-md-4 col-sm-6 mb-3">
            <div className="metric-card-analytics daily-revenue">
              <div className="metric-header">
                <span className="metric-value">{dashboardData.totalTransactions?.toLocaleString() || '0'}</span>
                <span className="metric-label">Transactions</span>
                <span className="metric-subtext">Total today</span>
              </div>
            </div>
          </div>
          
          <div className="col-lg-2 col-md-4 col-sm-6 mb-3">
            <div className="metric-card-analytics total-calls">
              <div className="metric-header">
                <span className="metric-value">{dashboardData.totalItems || 0}</span>
                <span className="metric-label">Items Sold</span>
                <span className="metric-subtext">Today</span>
              </div>
            </div>
          </div>
          
          <div className="col-lg-2 col-md-4 col-sm-6 mb-3">
            <div className="metric-card-analytics total-emails">
              <div className="metric-header">
                <span className="metric-value">{dashboardData.activeCashiers || 0}</span>
                <span className="metric-label">Active Cashiers</span>
                <span className="metric-subtext">Currently online</span>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content Row */}
        <div className="row">
          {/* Left Column - Payment Methods Pie Chart */}
          <div className="col-lg-3 col-md-12 mb-4">
            {/* Payment Methods */}
            <div className="analytics-panel agents-panel">
              <div className="panel-header">
                <h6>💳 PAYMENT METHODS</h6>
              </div>
              <div className="panel-content">
                {paymentMethodData && paymentMethodData.length > 0 ? (
                  <>
                    <ResponsiveContainer width="100%" height={250}>
                      <PieChart>
                        <Pie
                          data={paymentMethodData}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={({ name, percentage }) => `${percentage?.toFixed(1)}%`}
                          outerRadius={70}
                          fill="#8884d8"
                          dataKey="value"
                        >
                          {paymentMethodData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color || COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip 
                          formatter={(value, name, props) => [
                            `฿${parseFloat(value).toLocaleString('en-US', { minimumFractionDigits: 2 })}`,
                            props.payload.name
                          ]}
                          contentStyle={{
                            backgroundColor: '#ffffff',
                            border: '1px solid #bdc3c7',
                            borderRadius: '8px',
                            color: '#2c3e50'
                          }}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                    <div className="agents-list" style={{ marginTop: '15px' }}>
                      {paymentMethodData.map((payment, index) => (
                        <div key={index} className="agent-item" style={{
                          borderLeft: `4px solid ${payment.color}`,
                          marginBottom: '10px',
                          padding: '10px',
                          backgroundColor: `${payment.color}10`,
                          borderRadius: '4px'
                        }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '5px' }}>
                            <div style={{
                              width: '12px',
                              height: '12px',
                              borderRadius: '2px',
                              background: payment.color
                            }}></div>
                            <div className="agent-name" style={{ fontWeight: 600 }}>{payment.name}</div>
                          </div>
                          <div className="agent-stats">
                            <span className="stat-item">
                              <strong style={{ color: payment.color }}>฿{payment.value?.toLocaleString('en-US', { minimumFractionDigits: 2 }) || '0.00'}</strong>
                            </span>
                            <span className="stat-item">{payment.count} txn{payment.count !== 1 ? 's' : ''}</span>
                            <span className="stat-item">{payment.percentage?.toFixed(1) || '0.0'}%</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                ) : (
                  <div className="text-center py-3">
                    <p className="text-muted">No payment data available</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Center Column */}
          <div className="col-lg-6 col-md-3 col-sm-12 mb-4">
            {/* Yearly Comparison */}
            <div className="analytics-panel chart-panel">
              <div className="panel-header">
                <h6>YEARLY SALES COMPARISON</h6>
              </div>
              <div className="panel-content">
                {monthlySalesData && monthlySalesData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={monthlySalesData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#bdc3c7" />
                      <XAxis dataKey="year" stroke="#2c3e50" fontSize={12} />
                      <YAxis stroke="#2c3e50" fontSize={12} />
                      <Tooltip 
                        contentStyle={{
                          backgroundColor: '#ffffff',
                          border: '1px solid #bdc3c7',
                          borderRadius: '8px',
                          color: '#2c3e50'
                        }}
                      />
                      <Bar dataKey="sales" fill="#27ae60" name="Sales" />
                      <Bar dataKey="transactions" fill="#3498db" name="Transactions" />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="text-center py-5">
                    <p className="text-muted">No yearly comparison data available</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Right Column - Active Cashiers */}
          <div className="col-lg-3 col-md-3 col-sm-12 mb-4">
            <div className="analytics-panel status-panel">
              <div className="panel-header">
                <h6>👨‍💼 ACTIVE CASHIERS</h6>
              </div>
              <div className="panel-content" style={{ padding: '10px' }}>
                {cashierPerformanceData && cashierPerformanceData.length > 0 ? (
                  <div className="status-list">
                    {cashierPerformanceData.map((cashier, index) => (
                      <div key={index} style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '8px 10px',
                        marginBottom: '8px',
                        backgroundColor: '#f8f9fa',
                        borderRadius: '6px',
                        borderLeft: `3px solid ${cashier.color}`,
                        transition: 'all 0.2s ease',
                        cursor: 'pointer'
                      }}
                      onMouseOver={(e) => {
                        e.currentTarget.style.backgroundColor = '#e9ecef';
                        e.currentTarget.style.transform = 'translateX(3px)';
                      }}
                      onMouseOut={(e) => {
                        e.currentTarget.style.backgroundColor = '#f8f9fa';
                        e.currentTarget.style.transform = 'translateX(0)';
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}>
                          <div style={{
                            backgroundColor: cashier.color,
                            width: '28px',
                            height: '28px',
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'white',
                            fontWeight: 'bold',
                            fontSize: '12px',
                            flexShrink: 0
                          }}>
                            {cashier.name?.charAt(0) || 'C'}
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ 
                              fontWeight: 600, 
                              color: '#2c3e50',
                              fontSize: '13px',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis'
                            }}>
                              {cashier.name}
                            </div>
                            <div style={{ 
                              fontSize: '10px', 
                              color: '#95a5a6',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px'
                            }}>
                              <span>{cashier.transactions || 0} txns</span>
                              <span>•</span>
                              <span style={{
                                backgroundColor: cashier.color,
                                color: 'white',
                                padding: '1px 4px',
                                borderRadius: '3px',
                                fontSize: '9px',
                                fontWeight: 600
                              }}>
                                LIVE
                              </span>
                            </div>
                          </div>
                        </div>
                        <div style={{ textAlign: 'right', marginLeft: '8px' }}>
                          <div style={{ 
                            fontWeight: 700, 
                            color: cashier.color,
                            fontSize: '14px',
                            whiteSpace: 'nowrap'
                          }}>
                            ฿{cashier.sales?.toLocaleString('en-US', { maximumFractionDigits: 0 }) || '0'}
                          </div>
                          <div style={{ 
                            fontSize: '9px', 
                            color: '#95a5a6',
                            whiteSpace: 'nowrap'
                          }}>
                            revenue
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-4">
                    <div style={{ fontSize: '32px', opacity: 0.3 }}>👥</div>
                    <p className="text-muted" style={{ fontSize: '12px', marginTop: '8px' }}>No active cashiers</p>
                  </div>
                )}
              </div>
            </div>
          </div>
          
        </div>

        {/* Bottom Row */}
        <div className="row">
          {/* Daily Summary */}
          <div className="col-lg-4 col-md-3  col-sm-12 mb-4">
            <div className="analytics-panel sales-panel">
              <div className="panel-header">
                <h6>📋 TODAY'S SUMMARY</h6>
              </div>
              <div className="panel-content">
                <div className="sales-metrics">
                  <div className="sales-item">
                    <span className="sales-label">Total Sales</span>
                    <span className="sales-value">฿{dashboardData.todaySales?.toLocaleString() || '0'}</span>
                  </div>
                  <div className="sales-item">
                    <span className="sales-label">Transactions</span>
                    <span className="sales-value">{dashboardData.totalTransactions || '0'}</span>
                  </div>
                  <div className="sales-item profit">
                    <span className="sales-label">Avg Transaction</span>
                    <span className="sales-value">฿{dashboardData.totalTransactions > 0 ? (dashboardData.todaySales / dashboardData.totalTransactions).toFixed(2) : '0.00'}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Category-wise Sales Summary */}
          <div className="col-lg-4 col-md-6 mb-4">
            <div className="analytics-panel agents-ranking-panel">
              <div className="panel-header">
                <h6>📊 CATEGORY SALES (TODAY)</h6>
              </div>
              <div className="panel-content">
                {categorySalesData && categorySalesData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={250}>
                    <BarChart data={categorySalesData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#ecf0f1" />
                      <XAxis 
                        dataKey="category" 
                        stroke="#2c3e50" 
                        fontSize={11}
                        angle={-45}
                        textAnchor="end"
                        height={80}
                      />
                      <YAxis stroke="#2c3e50" fontSize={11} />
                      <Tooltip 
                        formatter={(value) => `฿${parseFloat(value).toLocaleString('en-US', { minimumFractionDigits: 2 })}`}
                        contentStyle={{
                          backgroundColor: '#ffffff',
                          border: '1px solid #bdc3c7',
                          borderRadius: '8px',
                          color: '#2c3e50'
                        }}
                      />
                      <Bar dataKey="sales" fill="#3498db" radius={[8, 8, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="text-center py-5">
                    <p className="text-muted">No category data available</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Daily Sales Trend Bar Chart */}
          <div className="col-lg-4 col-md-12 mb-4">
            <div className="analytics-panel suppliers-panel">
              <div className="panel-header">
                <h6>📈 DAILY SALES TREND (7 DAYS)</h6>
              </div>
              <div className="panel-content">
                {dailySalesData && dailySalesData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={250}>
                    <BarChart data={dailySalesData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#ecf0f1" />
                      <XAxis 
                        dataKey="day" 
                        stroke="#2c3e50" 
                        fontSize={11}
                      />
                      <YAxis stroke="#2c3e50" fontSize={11} />
                      <Tooltip 
                        formatter={(value) => `฿${parseFloat(value).toLocaleString('en-US', { minimumFractionDigits: 2 })}`}
                        labelFormatter={(label) => `${label}`}
                        contentStyle={{
                          backgroundColor: '#ffffff',
                          border: '1px solid #bdc3c7',
                          borderRadius: '8px',
                          color: '#2c3e50'
                        }}
                      />
                      <Bar dataKey="sales" fill="#2ecc71" radius={[8, 8, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="text-center py-5">
                    <p className="text-muted">No daily sales data available</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

      </div>
    </Layout>
  );
}

export default AnalyticsDashboard;
